import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Copy, Plus, Trash2, Check, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDateToDDMMYYYY } from '@/lib/utils';
import { useSecureAuth } from '@/hooks/useSecureAuth';

interface AdminCode {
  id: string;
  code_hash: string;
  is_active: boolean;
  used_count: number;
  max_uses: number;
  created_by: string;
  created_at: string;
  expires_at: string;
}

const AdminCodeGenerator: React.FC = () => {
  useUiLanguage();
  const [codes, setCodes] = useState<AdminCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const { generateSecureAdminCode } = useSecureAuth();

  useEffect(() => {
    fetchAdminCodes();
  }, []);

  const fetchAdminCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCodes(data || []);
    } catch (error) {
      console.error('Error fetching admin codes:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch admin codes"),
        variant: 'destructive'
      });
    }
  };

  const generateCode = async () => {
    setLoading(true);
    try {
      console.log('🔧 Generating admin code using secure function...');

      const adminCode = await generateSecureAdminCode(
        'SuperUser generated admin code',
        1, // max uses
        7  // valid for 7 days
      );

      if (adminCode) {
        setGeneratedCode(adminCode);
        toast({
          title: uiText("Success"),
          description: uiText("Admin code generated successfully"),
        });
        await fetchAdminCodes();
      } else {
        throw new Error(uiText("Failed to generate admin code"));
      }
    } catch (error: any) {
      console.error('Error generating admin code:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to generate admin code. Check console for details.',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: uiText("Copied"),
        description: uiText("Code copied to clipboard"),
      });
    } catch (error) {
      toast({
        title: uiText("Error"),
        description: uiText("Failed to copy to clipboard"),
        variant: 'destructive'
      });
    }
  };

  const deleteCode = async (codeId: string) => {
    try {
      const { error } = await supabase
        .from('admin_codes')
        .delete()
        .eq('id', codeId);

      if (error) throw error;

      fetchAdminCodes();
      toast({
        title: uiText("Code Deleted"),
        description: uiText("Admin code has been deleted"),
      });
    } catch (error) {
      console.error('Error deleting admin code:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete admin code"),
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Generate New Code */}
      <Card>
        <CardHeader>
          <CardTitle>{uiText("Generate New Admin Code")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={generateCode}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {loading ? uiText("Generating...") : uiText("Generate Admin Code")}
          </Button>

          {generatedCode && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">{uiText("Generated Code:")}</p>
              <div className="flex items-center gap-2">
                <Input
                  value={generatedCode}
                  readOnly
                  className="font-mono bg-background"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(generatedCode)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>{uiText("Existing Admin Codes")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{uiText("Code")}</TableHead>
                <TableHead>{uiText("Status")}</TableHead>
                <TableHead>{uiText("Created By")}</TableHead>
                <TableHead>{uiText("Created At")}</TableHead>
                <TableHead>{uiText("Expires At")}</TableHead>
                <TableHead>{uiText("Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell className="font-mono">{code.code_hash.substring(0, 12)}...</TableCell>
                  <TableCell>
                    <Badge variant={code.used_count >= code.max_uses ? 'destructive' : 'default'}>
                      {code.used_count >= code.max_uses ? (
                        <div className="flex items-center gap-1">
                          <X className="h-3 w-3" />
                          {uiText("Used (")}{code.used_count}/{code.max_uses})
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          {uiText("Available (")}{code.used_count}/{code.max_uses})
                        </div>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>{code.created_by || uiText("System")}</TableCell>
                  <TableCell>
                    {formatDateToDDMMYYYY(code.created_at)}
                  </TableCell>
                  <TableCell>
                    {code.expires_at ? formatDateToDDMMYYYY(code.expires_at) : uiText("Never")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(code.code_hash)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteCode(code.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {codes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {uiText("No admin codes generated yet")}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCodeGenerator;
